import React, { useState } from 'react';
import PropTypes from 'prop-types';

import restoreEmailTemplateRevision from '../../../api/graphql/mutations/restoreEmailTemplateRevision';
import formatTimestamp from '../../../utils/formatTimestamp';
import Notification, { useNotification } from '../../Notification';

// Screen 4 of the approved design: the revision timeline, opened from the
// press-check toolbar's Revisions button.
//
// Lists `template.revisions[]` newest-first - the server already keeps it in
// that order, capped at 10 (ps-back/util/email/revisionHistory.js) - under a
// synthetic first row for the version that is actually live, which is what the
// design's `Live` badge marks. The live row is the current server document, not
// a member of the revisions array, so it has no Restore action: it is already
// restored.
//
// `revisionIndex` passed to the mutation is the array position in
// `revisions`, which is exactly what restoreEmailTemplateRevision.js expects -
// the live row is not part of that array and does not shift it.
//
// Restoring is itself undoable: the resolver pushes the CURRENT (pre-restore)
// SERVER document onto the history before overwriting it, so a mis-click on a
// clean editor is recoverable by restoring again. But the restore mutation
// never sees the client's `draft` - it only reads/writes the server document.
// So if the editor has unsaved changes (`dirty`), those are gone the moment
// restore succeeds, and NOT covered by that recoverability. The confirm copy
// below is deliberately two different messages, not one blended one: it must
// not tell a dirty user "nothing is lost" when their unsaved edits are the
// one thing that IS lost - a reassuring lie is worse than no message. Restore
// is still allowed while dirty (it is a legitimate way to abandon a bad
// edit); the fix is an honest warning, not a block.
//
// After a successful restore this only reports the outcome; reloading the
// template (so the editor stops showing the stale pre-restore draft) is the
// parent's job via `onRestored`, matching the reload approach already used
// on the initial load in EmailTemplateEditor.jsx.
const EmailRevisionHistory = ({
  templateId, revisions, liveSavedAt, dirty, onRestored,
}) => {
  const [restoringIndex, setRestoringIndex] = useState(null);
  const [status, handleStatus] = useNotification();

  const handleRestore = async (revisionIndex) => {
    const confirmMessage = dirty
      ? 'Restore this version?\n\n'
        + 'You have unsaved changes in the editor. Those will be discarded and '
        + 'cannot be recovered.\n\n'
        + 'The last saved version is saved to history first, so you can undo the '
        + 'restore itself by restoring that version again.'
      : 'Restore this version? The current content will be replaced - but nothing is '
        + 'lost: the current version is saved to history first, so you can undo this by '
        + 'restoring it again.';

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) {
      return;
    }

    setRestoringIndex(revisionIndex);
    const result = await restoreEmailTemplateRevision({ templateId, revisionIndex });
    setRestoringIndex(null);

    if (result && result.isAuth && result.success && result.template) {
      handleStatus('icon-checkbox', 'Version restored.', 'green');
      if (onRestored) {
        onRestored();
      }
    } else if (result && result.isAuth) {
      handleStatus('icon-close-solid', result.error || 'Restore failed.', 'red');
    } else if (result) {
      handleStatus('icon-close-solid', 'You are not authorized to restore this version.', 'red');
    } else {
      handleStatus('icon-close-solid', 'Restore failed — check your connection and try again.', 'red');
    }
  };

  const kept = (revisions || []).length;

  return (
    <div className="panel">
      <div className="head">
        <h4>Revisions</h4>
        <div className="right">
          <span className="locked">{`${kept} kept`}</span>
        </div>
      </div>

      {status.text && (
        <div className="notice">
          <Notification {...status} />
        </div>
      )}

      <div className="rev current">
        <span className="dot"><i /></span>
        <span>
          <span className="who"><b>Current version</b></span>
          <span className="meta">{formatTimestamp(liveSavedAt)}</span>
        </span>
        <span className="badge">Live</span>
      </div>

      {kept === 0 ? (
        <div className="body">
          <p className="hint">
            No earlier versions yet — saving this plate again records the version it
            replaced here, up to the last ten.
          </p>
        </div>
      ) : (
        revisions.map((revision, index) => (
          <div className="rev" key={`${revision.savedAt}|${revision.author}`}>
            <span className="dot"><i /></span>
            <span>
              <span className="who">
                <b>{revision.author || 'unknown'}</b>
                {' saved this version'}
              </span>
              <span className="meta">{formatTimestamp(revision.savedAt)}</span>
            </span>
            <button
              type="button"
              className="btn tiny ghost"
              onClick={() => handleRestore(index)}
              disabled={restoringIndex !== null}
            >
              {restoringIndex === index ? 'Restoring…' : 'Restore'}
            </button>
          </div>
        ))
      )}
    </div>
  );
};

EmailRevisionHistory.propTypes = {
  templateId: PropTypes.string.isRequired,
  revisions: PropTypes.arrayOf(PropTypes.shape({
    subject: PropTypes.string,
    preheader: PropTypes.string,
    htmlContent: PropTypes.string,
    textContent: PropTypes.string,
    author: PropTypes.string,
    savedAt: PropTypes.string,
  })),
  liveSavedAt: PropTypes.string,
  dirty: PropTypes.bool,
  onRestored: PropTypes.func,
};

EmailRevisionHistory.defaultProps = {
  revisions: [],
  liveSavedAt: null,
  dirty: false,
  onRestored: null,
};

export default EmailRevisionHistory;
